import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PAYMENT_METHODS } from '@/utils/constants';
import { FormData } from './formSchema';

interface PaymentMethodSectionProps {
  form: UseFormReturn<FormData>;
}

export default function PaymentMethodSection({ form }: PaymentMethodSectionProps) {
  const watchedPaymentMethod = form.watch('paymentMethod.type');

  // Get threshold based on payment method using constants
  const getThresholdForPaymentMethod = (paymentMethod: string) => {
    const method = Object.values(PAYMENT_METHODS).find(m => m.type === paymentMethod);
    return method ? method.threshold : 75; // Default to 75 if method not found
  };

  // Render payment details fields based on selected method
  const renderPaymentDetailsFields = () => {
    switch (watchedPaymentMethod) {
      case 'paypal':
        return (
          <FormField
            control={form.control}
            name="paymentDetails.paypalEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>PayPal Email</FormLabel>
                <FormControl>
                  <Input placeholder="author@paypal.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case 'bank_transfer_us':
        return (
          <>
            <FormField
              control={form.control}
              name="paymentDetails.bankName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Bank of America" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentDetails.accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input placeholder="1234567890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentDetails.routingNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Routing Number</FormLabel>
                  <FormControl>
                    <Input placeholder="021000021" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        );
      case 'bank_transfer_international':
        return (
          <>
            <FormField
              control={form.control}
              name="paymentDetails.bankName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name</FormLabel>
                  <FormControl>
                    <Input placeholder="International Bank" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentDetails.accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input placeholder="1234567890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentDetails.swift"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SWIFT Code</FormLabel>
                  <FormControl>
                    <Input placeholder="ABCDUS33XXX" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentDetails.address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter bank address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        );
      case 'check':
        return (
          <FormField
            control={form.control}
            name="paymentDetails.address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mailing Address</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="123 Main St, City, State, ZIP" 
                    className="min-h-[80px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="border-t pt-4">
      <h3 className="text-lg font-medium mb-4">Payment Information</h3>
      
      <FormField
        control={form.control}
        name="paymentMethod.type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Payment Method</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="bank_transfer_us">US Bank Transfer</SelectItem>
                <SelectItem value="bank_transfer_international">International Bank Transfer</SelectItem>
                <SelectItem value="check">Check</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="mt-4 p-3 bg-muted rounded-md">
        <p className="text-sm text-muted-foreground">
          <strong>Payment Threshold:</strong> ${getThresholdForPaymentMethod(watchedPaymentMethod)}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Minimum balance required before payment is processed for this payment method.
        </p>
      </div>

      {renderPaymentDetailsFields()}
    </div>
  );
}
